import React, { useRef, useState } from "react"
import { Formik, Form, FieldArray } from "formik"
import { Accordion, AccordionSummary, Badge, Stack } from "@mui/material"
import { FormControl, TextField, Select, InputLabel } from "@mui/material"
import { MenuItem } from "@mui/material"
import HoursMaskedField from "../../../components/form/MaskedField"
import dayjs from "dayjs"
import { PSE_BLOCK_TYPES, PSE_REGEX_HOURS } from "../../../utils/productionStepExecution"
import { COLORS } from "../../../utils"
import { isEmpty } from "lodash"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { BUTTON_STATUS } from "../../../utils/constant"

export const productionStepExecutionCookingEndFormId = "productionStepExecutionCookingForm"
export const productionStepExecutionCoolingEndFormId = "productionStepExecutionCoolingEndForm"
export const productionStepExecutionCoolingFormId = "productionStepExecutionCoolingForm"

const fieldsLibelle = {
  machineName: "Machine",
  coolingStartTime: "Heure de début",
  startTemperature: "Température de début",
  coolingEndTime: "Heure de fin",
  endTemperature: "Température de fin",
  cookingEndTime: "Heure de fin de cuisson",
}


const MachineReadOnlyCard = ({ machineBatch, mode, index, withBadge, handleChangeTemperatures }) => {
  let fieldsToShow = ["machineName", "cookingEndTime", "endTemperature"]
  if (mode === PSE_BLOCK_TYPES.COOLING_END) {
    fieldsToShow = ["machineName", "coolingEndTime", "endTemperature"]
  } else if (mode === PSE_BLOCK_TYPES.COOLING) {
    fieldsToShow = ["machineName", "coolingStartTime", "startTemperature"]
  }
  const isTemperatureOnly = !handleChangeTemperatures
  const handleChangeTemperaturesWrapper = (event) => {
    const fieldName = event.target.name
    const newValue = event.target.value
    handleChangeTemperatures(index, {
      [fieldName]: newValue
    })
    machineBatch[fieldName] = newValue
  }
  return (<Stack sx={{ ...sx.formContainer, ...sx.formResume }} className={mode + "readOnly" + (withBadge ? "withBadge" : "") + (isTemperatureOnly ? " temperatureEdit" : "temperatureEditOff temperatureEditOff")} direction="row">
    <Stack sx={sx.subtitleContainer} direction="row" gap={2} alignItems="center" justifyContent={"space-between"}>
      <InputLabel sx={sx.cardLabel} variant="standard" className="title">
        {mode === PSE_BLOCK_TYPES.COOLING ? "Début" : "Fin"}
      </InputLabel>
      {withBadge &&
        <Badge color="error" sx={sx.subtitleBadge} className="completed-badge">
          Complétée
        </Badge>}
    </Stack>
    {fieldsToShow.map((field) => (
      <>{field.includes("Temperature") && handleChangeTemperatures ?
        <FormControl sx={{ flex: 1 }}>
          <TextField
            name={field}
            value={machineBatch[field]}
            label={fieldsLibelle[field]}
            onChange={handleChangeTemperaturesWrapper}
            variant="standard"
            type="number"
            InputProps={{
              inputProps: { step: "any" },
              endAdornment: <span>°C</span>
            }}
          /></FormControl> :
        <Stack key={field}>
          <InputLabel variant="standard" sx={sx.resumeLabel}>{fieldsLibelle[field]}</InputLabel>
          <InputLabel variant="standard" sx={sx.resumeValue}>
            {field.includes("Time") ? (machineBatch[field] && machineBatch[field].includes && machineBatch[field].includes(":") ? machineBatch[field] : dayjs(machineBatch[field]).format("HH:mm")) : `${machineBatch[field]}${field.includes("Temperature") ? " °C" : ""}`}
          </InputLabel>
        </Stack>
      }
      </>
    ))}
  </Stack>)
}


const isCompleted = (machineBatch, mode) => {
  switch (mode) {
    case "cooling":
      return (machineBatch.machineId || machineBatch.machine?.objectId) && (machineBatch.coolingStartTime && machineBatch.coolingStartTime !== "__:__") && machineBatch.startTemperature != null
    case "coolingEnd":
      return (machineBatch.machineId || machineBatch.machine?.objectId) && (machineBatch.coolingEndTime && machineBatch.coolingEndTime !== "__:__") && machineBatch.endTemperature != null
    default:
      return (machineBatch.machineId || machineBatch.machine?.objectId) && (machineBatch.cookingEndTime && machineBatch.cookingEndTime !== "__:__") && machineBatch.endTemperature != null
  }
}

const ProductionStepExecutionMachinesForm = ({
  mode = "cooking",
  machinesBatch = [],
  machinesOptions = [],
  onSubmit,
  onError,
  setNextButtonStatus,
  handleChangeTemperatures
}) => {
  const getCompletedIndexes = (machines, reverse) => {
    return machines.reduce((acc, machine, index) => {
      const completed = isCompleted(machine, mode)
      const condition = reverse ? !completed : completed
      if (condition) acc.push(index)
      return acc
    }, [])
  }
  const completedIndexes = useRef(getCompletedIndexes(machinesBatch))
  const [expandedIndexes, setExpandedIndexes] = useState(getCompletedIndexes(machinesBatch, true))
  let firstEmptyFieldFound = useRef(false)


  const allowPartialCompletion = !!setNextButtonStatus
  const isReadOnly = !onSubmit

  const handleSubmit = async (values) => {
    onSubmit && await onSubmit(values)
    const allIncompletes = getCompletedIndexes(values.machinesBatch, true)
    if (!allIncompletes.length) return
    setExpandedIndexes(allIncompletes)
    completedIndexes.current = getCompletedIndexes(values.machinesBatch)
  }

  const handleToggle = (index) => {
    setExpandedIndexes(prev => (prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]))
  }
  const handleValidate = (values) => {
    const errors = {}
    const machinesBatchErrors = []
    const { machinesBatch } = values
    for (const machineBatch of machinesBatch) {
      const index = machinesBatch.indexOf(machineBatch)
      const machineBatchError = {}
      if (!machineBatch.machineId || typeof machineBatch.machineId !== "string") {
        machineBatchError.machineId = "Champ obligatoire"
      }
      if (!machineBatch.machineName || typeof machineBatch.machineName !== "string") {
        machineBatchError.machineName = "Champ obligatoire"
      }
      if (mode === "cooling") {
        if (!machineBatch.coolingStartTime || PSE_REGEX_HOURS.test(machineBatch.coolingStartTime) === false) {
          machineBatchError.coolingStartTime = "Veuillez entrer une heure valide"
        }
        if (machineBatch.startTemperature == null || typeof machineBatch.startTemperature !== "number") {
          machineBatchError.startTemperature = "Veuillez saisir la température à laquelle votre préparation a commencé son refroidissement."
        }
      } else if (mode === "coolingEnd") {
        if (!machineBatch.coolingEndTime || PSE_REGEX_HOURS.test(machineBatch.coolingEndTime) === false) {
          machineBatchError.coolingEndTime = "Veuillez entrer une heure valide"
        }
        if (machineBatch.endTemperature == null || typeof machineBatch.endTemperature !== "number") {
          machineBatchError.endTemperature = "Veuillez saisir la température à laquelle votre préparation a fini son refroidissement."
        }
      } else {
        if (!machineBatch.cookingEndTime || PSE_REGEX_HOURS.test(machineBatch.cookingEndTime) === false) {
          machineBatchError.cookingEndTime = "Veuillez entrer une heure valide"
        }
        if (machineBatch.endTemperature == null || typeof machineBatch.endTemperature !== "number") {
          machineBatchError.endTemperature = "Veuillez saisir la température à laquelle votre préparation a fini sa cuisson."
        }
      }
      if (!isEmpty(machineBatchError)) {
        machinesBatchErrors[index] = machineBatchError
      }
    }
    if (machinesBatchErrors.length > 0 && (!allowPartialCompletion || machinesBatchErrors.filter(Boolean).length === machinesBatch.length)) {
      errors.machinesBatch = machinesBatchErrors
    }
    if (errors.machinesBatch) {
      const allErrors = errors.machinesBatch.reduce((acc, curr) => ({ ...acc, ...curr }), {})
      onError && onError([...new Set(Object.values(allErrors))].join("\r\n"))
    }
    return errors
  }

  const getInitialValues = (machine) => {
    switch (mode) {
      case "cooling":
        return {
          machineId: machine.machine?.objectId,
          machineName: machine.machineName,
          coolingStartTime: dayjs(machine.startTemperature ? machine.coolingStartTime : undefined).format("HH:mm"),
          startTemperature: machine.startTemperature
        }
      case "coolingEnd":
        return {
          machineId: machine.machine?.objectId,
          machineName: machine.machineName,
          coolingStartTime: machine.coolingStartTime,
          startTemperature: machine.startTemperature,
          coolingEndTime: !isReadOnly ? dayjs(machine.endTemperature ? machine.coolingEndTime : undefined).format("HH:mm") : machine.coolingEndTime,
          endTemperature: machine.endTemperature
        }
      default:
        return {
          machineId: machine.machine?.objectId,
          machineName: machine.machineName,
          cookingEndTime: !isReadOnly ? dayjs(machine.endTemperature ? machine.cookingEndTime : undefined).format("HH:mm") : machine.cookingEndTime,
          endTemperature: machine.endTemperature
        }
    }
  }

  return (
    <Formik
      initialValues={{
        machinesBatch: machinesBatch.map(getInitialValues)
      }}
      validate={handleValidate}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({ values, errors, handleChange, setFieldValue }) => {
        const completionMachinesStatus = values.machinesBatch.map((machineBatch, index) => {
          return {
            isCompleted: isCompleted(machineBatch, mode),
            machineName: machineBatch.machineName || `Machine n° ${index + 1}`
          }
        })
        const [focusedRecipeIndex, setFocusedRecipeIndex] = React.useState(null)

        values.machinesBatch.forEach((_, index) => {
          Object.keys(values.machinesBatch[index]).forEach((field) => {
            const fieldName = `machinesBatch.${index}.${field}`
            const inputElement = document.querySelector(`[name="${fieldName}"]`)

            const focusHandler = () => {
              setFocusedRecipeIndex(index)
            }

            const blurHandler = () => {
              setFocusedRecipeIndex(null)
            }

            if (inputElement) {
              inputElement.removeEventListener("focus", focusHandler) // Unsubscribe previous focus listener
              inputElement.removeEventListener("blur", blurHandler) // Unsubscribe previous blur listener
              inputElement.addEventListener("focus", focusHandler) // Subscribe new focus listener
              inputElement.addEventListener("blur", blurHandler) // Subscribe new blur listener

              // Autofocus the first empty field
              if (!firstEmptyFieldFound.current && !values.machinesBatch[index][field]) {
                inputElement.focus()
                firstEmptyFieldFound.current = true
              }
            }
          })
        })

        const allMachinesCompleted = completionMachinesStatus.every((machine) => machine.isCompleted)
        const someMachinesCompleted = completionMachinesStatus.some((machine) => machine.isCompleted)

        setNextButtonStatus && setNextButtonStatus(allMachinesCompleted ? BUTTON_STATUS.ENABLED : someMachinesCompleted ? BUTTON_STATUS.SAVE_ONLY : BUTTON_STATUS.DISABLED)

        const _renderBadge = (index) => {
          return (
            <Badge color="error" sx={sx.subtitleBadge} className={completionMachinesStatus[index].isCompleted ? "completed-badge" : ""}>
              {completionMachinesStatus[index].isCompleted ? "Complétée" : "À compléter"}
            </Badge>
          )
        }

        return (
          <Form
            id={mode === "cooking" ? productionStepExecutionCookingEndFormId : productionStepExecutionCoolingEndFormId}
          >
            <FieldArray name="machinesBatch">
              {() => (
                <Stack>
                  {values.machinesBatch?.map((_, index) => (
                    <Accordion
                      expanded={expandedIndexes.includes(index)}
                      onChange={() => handleToggle(index)}
                      disableGutters
                      elevation={0}
                      key={index}
                      direction="column"
                      justifyContent="space-between"
                      gap={2}
                      sx={{ ...sx.accordion, mb: isReadOnly ? 3 : 0 }}
                    >
                      {!isReadOnly &&
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls={`panel-${index}-content`}
                          id={`panel-${index}-header`}
                          sx={sx.accordionSummary}
                        >
                          <InputLabel sx={sx.machineLabel} id="test-select-label" variant="standard">Machine n° {index + 1} :</InputLabel>
                          {!expandedIndexes.includes(index) && _renderBadge(index)}
                          {/* Ton header ici */}
                        </AccordionSummary>}
                      <Stack
                        direction="row" gap={isReadOnly ? 3 : 1.5}>
                        {(mode === PSE_BLOCK_TYPES.COOLING_END) && <MachineReadOnlyCard machineBatch={values.machinesBatch[index]} index={index} mode={PSE_BLOCK_TYPES.COOLING} handleChangeTemperatures={handleChangeTemperatures} />}
                        {isReadOnly || completedIndexes.current.includes(index) ? <MachineReadOnlyCard machineBatch={values.machinesBatch[index]} index={index} mode={mode} withBadge={!isReadOnly && completedIndexes.current.includes(index)} handleChangeTemperatures={handleChangeTemperatures} /> : (
                          <Stack className="form-container" sx={sx.formContainer} direction="row" gap={2} style={{ borderColor: focusedRecipeIndex === index ? COLORS.PRIMARY_COLOR : "" }}>
                            <Stack sx={sx.subtitleContainer} direction="row" gap={2} alignItems="center" justifyContent={"space-between"}>
                              <InputLabel sx={sx.cardLabel} variant="standard">
                                {mode === "cooling" ? "Début" : "Fin"}
                              </InputLabel>
                              {_renderBadge(index)}
                            </Stack>
                            <FormControl sx={{ flex: 1 }}>
                              <InputLabel id="test-select-label" variant="standard">Machine</InputLabel>
                              <Select
                                variant="standard"
                                value={values.machinesBatch[index].machineId}
                                name={`machinesBatch.${index}.machineId`}
                                onChange={(e) => {
                                  const selectedMachine = machinesOptions.find(machine => machine.objectId === e.target.value)
                                  setFieldValue(`machinesBatch.${index}.machineName`, selectedMachine.name)
                                  setFieldValue(`machinesBatch.${index}.machineId`, e.target.value)
                                }}
                              >
                                {machinesOptions.map((machine) => (
                                  <MenuItem key={machine.objectId} value={machine.objectId}>
                                    {machine.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {mode === "cooking" && <FormControl sx={sx.hoursMaskedFieldForm}>
                              <HoursMaskedField
                                name={`machinesBatch.${index}.cookingEndTime`}
                                value={values.machinesBatch[index].cookingEndTime}
                                onChange={handleChange}
                                label={fieldsLibelle.cookingEndTime}
                                width="100%"
                                hasError={!!errors?.machinesBatch?.[index]?.cookingEndTime}
                              />
                            </FormControl>}
                            {mode === "cooking" && <FormControl sx={{ flex: 1 }}>
                              <TextField
                                name={`machinesBatch.${index}.endTemperature`}
                                value={values.machinesBatch[index].endTemperature}
                                label="Température"
                                onChange={handleChange}
                                variant="standard"
                                type="number"
                                InputProps={{
                                  inputProps: { step: "any" },
                                  endAdornment: <span>°C</span>
                                }}
                                onWheel={(e) => e.target.blur()}
                                sx={{
                                  "& .MuiInput-underline:before, .MuiInput-underline:after": {
                                    borderBottomColor: errors?.machinesBatch?.[index]?.endTemperature && "#F44259"
                                  }
                                }}
                              />
                            </FormControl>}
                            {mode === "cooling" && <FormControl sx={sx.hoursMaskedFieldForm}>
                              <HoursMaskedField
                                name={`machinesBatch.${index}.coolingStartTime`}
                                value={values.machinesBatch[index].coolingStartTime}
                                onChange={handleChange}
                                label="Heure de début de refroidissement"
                                width="100%"
                                hasError={!!errors?.machinesBatch?.[index]?.coolingStartTime}
                              />
                            </FormControl>}
                            {mode === "cooling" && <FormControl sx={{ flex: 1 }}>
                              <TextField
                                name={`machinesBatch.${index}.startTemperature`}
                                value={values.machinesBatch[index].startTemperature}
                                label="Température"
                                onChange={handleChange}
                                variant="standard"
                                type="number"
                                InputProps={{
                                  inputProps: { step: "any" },
                                  endAdornment: <span>°C</span>
                                }}
                                onWheel={(e) => e.target.blur()}
                                sx={{
                                  "& .MuiInput-underline:before, .MuiInput-underline:after": {
                                    borderBottomColor: errors?.machinesBatch?.[index]?.startTemperature && "#F44259"
                                  }
                                }}
                              />
                            </FormControl>}
                            {mode === "coolingEnd" && <FormControl sx={sx.hoursMaskedFieldForm}>
                              <HoursMaskedField
                                name={`machinesBatch.${index}.coolingEndTime`}
                                value={values.machinesBatch[index].coolingEndTime}
                                onChange={handleChange}
                                label="Heure de fin"
                                width="100%"
                                hasError={!!errors?.machinesBatch?.[index]?.coolingEndTime}
                              />
                            </FormControl>}
                            {mode === "coolingEnd" && <FormControl sx={{ flex: 1 }}>
                              <TextField
                                name={`machinesBatch.${index}.endTemperature`}
                                value={values.machinesBatch[index].endTemperature}
                                label="Température de fin"
                                onChange={handleChange}
                                variant="standard"
                                type="number"
                                InputProps={{
                                  inputProps: { step: "any" },
                                  endAdornment: <span>°C</span>
                                }}
                                onWheel={(e) => e.target.blur()}
                                sx={{
                                  "& .MuiInput-underline:before, .MuiInput-underline:after": {
                                    borderBottomColor: errors?.machinesBatch?.[index]?.endTemperature && "#F44259"
                                  }
                                }}
                              />
                            </FormControl>}
                          </Stack>)}
                      </Stack>
                    </Accordion>
                  ))}
                </Stack>
              )}
            </FieldArray>
          </Form>
        )
      }}
    </Formik>
  )

}

const sx = {
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    border: "1px solid #E0E0E0",
    borderRadius: "8px",
    padding: "24px",
    marginBottom: "16px",
    marginTop: "8px",
    flex: 1,
    "& .MuiSelect-select, .MuiInput-input": {
      fontSize: "18px !important",
    },
    "& .MuiFormControl-root": {
      marginTop: "8px"
    },
    "& .MuiInputLabel-root": {
      fontSize: "18px"
    },
    "& .MuiInput-underline": {
      marginTop: "16px !important"
    },
    "& .MuiInputLabel-shrink": {
      fontSize: "16px",
      transform: "translate(0, -12px) scale(1)"
    }
  },
  accordion: {
    "&:before": {
      display: "none"
    }
  },
  accordionSummary: {
    padding: 0,
    "& .MuiAccordionSummary-content": {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center"
    }
  },
  formResume: {
    "&:not(.temperatureEditOff)": {
      border: "none",
      backgroundColor: COLORS.BLACK_50,
    },
    "&.cookingreadOnly": {
      flexDirection: "row",
      flexWrap: "wrap",
      "& > :first-child": {
        width: "100%",
        flex: "none"
      }
    }
  },
  resumeValue: {
    marginTop: "10px",
    marginBottom: "6px",
    fontWeight: 400,
    fontSize: 18,
    color: COLORS.BLACK800
  },
  resumeLabel: {
    fontSize: "16px !important"
  },
  subtitleContainer: {
    position: "relative",
  },
  subtitleBadge: {
    color: "#B38616",
    backgroundColor: COLORS.ORANGE_100,
    padding: "2px 8px",
    borderRadius: "16px",
    fontFamily: "Roboto",
    fontSize: "14px",
    border: "1px solid " + COLORS.ORANGE_200,
    "&.completed-badge": {
      color: COLORS.GREEN_800,
      backgroundColor: COLORS.GREEN_50,
      border: "1px solid " + COLORS.GREEN_300,
    }
  },
  machineLabel: {
    fontWeight: 500,
    fontSize: 20,
    color: COLORS.BLACK800
  },
  cardLabel: {
    fontWeight: 500,
    color: COLORS.BLACK800,
    fontSize: 18
  },
  hoursMaskedFieldForm: {
    flex: 1,
    "& input": {
      textAlign: "left !important"
    }
  }
}
export default ProductionStepExecutionMachinesForm